import { Fragment, useContext, useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "@/components/bootstrap/TemporaryReactstrapCompatibilityLayer";
import Button from "../layout/Button";
import AuthContext from "../../contexts/auth/AuthContext";
import UIContext from "../../contexts/ui/UIContext";
import ProcessContext from "../../contexts/process/ProcessContext";
import { HELP_DESK_LINK } from "../../constants/helpDesk";
import ConfirmDeleteModal from "../management/seedTypes/ConfirmDeleteModal";
import UsersApiService from "../../services/apiServices/UsersApiService";

const GlobalUserEditor = ({ user, changeRoleHandler, reloadHandler }) => {
  const { userInformation } = useContext(AuthContext);
  const { addNotification } = useContext(UIContext);
  const { notifyError } = useContext(ProcessContext);

  const initialState = {
    email: {
      value: user.email ?? "",
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: "Email ...",
    },
    id: {
      value: user.id ?? "",
      touched: true,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
    isGlobalAdmin: {
      value: user.isGlobalAdmin ?? 0,
      touched: true,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
    familyName: {
      value: user.familyName ?? "Not yet provided",
      touched: true,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
    givenName: {
      value: user.givenName ?? "Not yet provided",
      touched: true,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
  };

    const [userState, setUserState] = useState(initialState);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSelf = userInformation?.userId === user?.id;

  Object.values(userState)
    .map((entry) => entry.isValid)
    .every((item) => !!item);

  useEffect(() => {
    if (!user) return;
    const next = Object.entries(user).reduce((acc, [key, value]) => {
      if (!(key in userState)) return acc;
      return {
        ...acc,
        [key]: { ...acc[key], value, isValid: acc[key]?.validate(value) },
      };
    }, initialState);
    setUserState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onChange = (event) => {
    const key = event.currentTarget.name;
    const value = event.currentTarget.value;
    setUserState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        isValid: prev[key].validate(value),
        touched: true,
      },
    }));
  };


  const handleDelete = () => {
    if (userInformation?.userId === user?.id) {
      addNotification("You cannot delete your own account.", "warning");
      return;
    }
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (userInformation?.id === user?.id) {
      setShowConfirmDelete(false);
      return;
    }

    setShowConfirmDelete(false);
    setDeleting(true);

    try {
      await UsersApiService.deleteUser(user.id);
      addNotification("Successfully deleted user.", "success");
    } catch (error) {
      if (error.message === "Not found") {
        notifyError(
          <>
            We have encountered an issue while deleting the user. Could you
            please logout, sign back in, and try again? If the error still
            exists, please contact the support team {HELP_DESK_LINK}.
          </>
        );
      } else {
        notifyError(
          <>
            We have encountered an issue on our side while deleting the user.
            Please contact the support team {HELP_DESK_LINK}.
          </>
        );
      }
      console.error(error);
    } finally {
      setDeleting(false);
      await reloadHandler?.();
    }
  };

  const changeGlobalAdminStatus = () => {
    if (userInformation?.userId === user?.id) {
      addNotification("You cannot change your own role.", "warning");
      return;
    }
    changeRoleHandler(user);
  };

  const combineNames = () => {
    if (!userState.familyName.value || !userState.givenName.value) {
      return "No name provided yet";
    }
    return `${userState.familyName.value}, ${userState.givenName.value}`;
  };

  return (
    <>
      <ConfirmDeleteModal
        show={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        header="DELETE USER"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        message={
          <>
            Deleting this user will immediately revoke access to the application
            and related resources.
            <br />
            This action cannot be undone.
          </>
        }
      />

      <ConfirmDeleteModal
        show={deleting}
        hideActions
        header="Deleting user..."
        message={
          <>
            <p>Please wait while the user is being removed.</p>
            <i className="fas fa-cog fa-spin" />
          </>
        }
      />

<Card className="seed-card global-user-card">
        <CardHeader className="seed-card-body">
          <Fragment>
            <input
              className={
                userState.familyName.isValid || !userState.familyName.touched
                  ? "h4-input seed-property-input-field header-input"
                  : "h4-input seed-property-input-field header-input invalid-field"
              }
              type="text"
              minLength="3"
              value={combineNames()}
              onChange={onChange}
              name="name"
              id="name"
              placeholder={userState.familyName.placeholder}
              disabled={true}
            />
          </Fragment>
        </CardHeader>

        <CardBody className="global-user-card-body">
    <div className="card-body-input-row">
      <label className="input-label" htmlFor="email">Email&nbsp;</label>

      <input
        className={
          userState.email.isValid || !userState.email.touched
            ? "seed-property-input-field"
            : "seed-property-input-field invalid-field"
        }
        type="text"
        value={userState.email.value}
        disabled
      />
    </div>
  </CardBody>

  <div className="global-user-card-buttons">
    <Button
      className="global-user-card-btn"
      label="Delete User"
      clickHandler={handleDelete}
      disabled={isSelf}
      type="inline"
    />

    <Button
      className="global-user-card-btn"
      label={user?.isGlobalAdmin ? "Global Admin" : "Regular User"}
      icon={user?.isGlobalAdmin ? "fas fa-globe" : "far fa-user"}
      clickHandler={changeGlobalAdminStatus}
      disabled={isSelf}
      type="inline"
    />
  </div>
</Card>
    </>
  );
};

export default GlobalUserEditor;
